import { LinearProgress, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { OptionType, SGSSelectProps } from 'components';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ListChildComponentProps, VariableSizeList } from 'react-window';
import styles from './AutoComplete.module.scss';

// Virtualisation react-window
// https://github.com/bvaughn/react-window

// Intégration de la virtualisation basé sur la documentation de MaterialUI
// https://mui.com/material-ui/react-autocomplete/

type Props = SGSSelectProps & {
    loading?: boolean;
    handleChange?: Function;
    disableCloseOnSelect?: boolean;
    variant?: string;
};

function renderRow(props: ListChildComponentProps) {
    const { data, index, style } = props;
    return React.cloneElement(data[index], {
        style: {
            ...style,
            fontFamily: 'Open Sans',
            top: style.top as number,
        },
    });
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
    const outerProps = React.useContext(OuterElementContext);
    return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data: any) {
    const ref = React.useRef<VariableSizeList>(null);
    React.useEffect(() => {
        if (ref.current != null) {
            ref.current.resetAfterIndex(0, true);
        }
    }, [data]);
    return ref;
}

const ListboxComponent = React.forwardRef<HTMLDivElement>(function ListboxComponent(props, ref) {
    const { children, ...other } = props;
    const itemData = React.Children.toArray(children);
    const itemCount = itemData.length;
    const itemSize = 30;

    const getChildSize = (child: React.ReactNode) => {
        if (React.isValidElement(child)) {
            return 30;
        }

        return itemSize;
    };

    const getHeight = () => {
        if (itemCount > 16) {
            return 16 * itemSize;
        }
        return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
    };

    const gridRef = useResetCache(itemCount);

    return (
        <div ref={ref}>
            <OuterElementContext.Provider value={other}>
                <VariableSizeList
                    itemData={itemData}
                    width="100%"
                    height={getHeight() + 2}
                    ref={gridRef}
                    itemSize={(index) => getChildSize(itemData[index])}
                    outerElementType={OuterElementType}
                    innerElementType="ul"
                    overscanCount={5}
                    itemCount={itemCount}
                >
                    {renderRow}
                </VariableSizeList>
            </OuterElementContext.Provider>
        </div>
    );
});

const SGSFormAutoComplete: React.FC<Props> = ({ name, label, options, variant = 'outlined', loading, error, handleChange, disableCloseOnSelect = false, ...props }) => {
    const { control } = useFormContext();
    const { t } = useTranslation();

    return (
        <Controller
            {...props}
            name={name}
            control={control}
            render={({ field: { onChange, ref }, fieldState: { error } }) => (
                <>
                    {loading && <LinearProgress className={loading ? styles.showLoader : styles.hideLoader} />}

                    <Autocomplete
                        onChange={(_, data: OptionType<string>) => {
                            if (handleChange) {
                                handleChange(data);
                                onChange(data ? (props.multiple ? data : data.value) : '');
                            } else {
                                onChange(data ? data.value : '');
                            }
                        }}
                        id={name}
                        value={props.value}
                        ref={ref}
                        multiple={props.multiple}
                        limitTags={5}
                        disableCloseOnSelect={disableCloseOnSelect}
                        defaultValue={props.defaultValue}
                        disabled={loading || props.disabled}
                        noOptionsText="Pas de résultat"
                        disableListWrap
                        fullWidth
                        ListboxComponent={ListboxComponent as React.ComponentType<React.HTMLAttributes<HTMLElement>>}
                        options={options}
                        renderInput={(params) => <TextField error={!!error} {...params} label={t(label as string)} helperText={error?.message} variant={variant} />}
                        getOptionLabel={(option: any) => (option?.label ? option?.label?.toString() : ' ')}
                    />
                </>
            )}
        />
    );
};

export default SGSFormAutoComplete;
